import React, { useState } from 'react';
import {
  User,
  GraduationCap,
  BarChart3,
  Target,
  Users,
  Trophy,
  ChevronDown,
  ChevronUp,
  FileText,
  Plus,
  X,
  Sparkles
} from 'lucide-react';

const Step1_QuickProfileInput = ({ wizardData, updateData, goToNextStep }) => {
  const { profile } = wizardData;
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    academic: false,
    scores: false,
    coursework: false,
    interests: false,
    extracurriculars: false,
    honors: false
  });

  const updateProfile = (field, value) => {
    updateData('profile', {
      ...profile,
      [field]: value
    });
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const addActivity = () => {
    const newextracurriculars = [...(profile.extracurriculars || []), {
      name: '',
      category: 'academic',
      years_participated: '',
      hours_per_week: '',
      description: ''
    }];
    updateProfile('extracurriculars', newextracurriculars);
  };

  const updateActivity = (index, field, value) => {
    const updated = [...profile.extracurriculars];
    updated[index] = { ...updated[index], [field]: value };
    updateProfile('extracurriculars', updated);
  };

  const removeActivity = (index) => {
    const updated = profile.extracurriculars.filter((_, i) => i !== index);
    updateProfile('extracurriculars', updated);
  };

  const addHonor = () => {
    const newHonors = [...(profile.honors || []), {
      name: '',
      level: 'school',
      date_received: '',
      description: ''
    }];
    updateProfile('honors', newHonors);
  };

  const updateHonor = (index, field, value) => {
    const updated = [...profile.honors];
    updated[index] = { ...updated[index], [field]: value };
    updateProfile('honors', updated);
  };

  const removeHonor = (index) => {
    const updated = profile.honors.filter((_, i) => i !== index);
    updateProfile('honors', updated);
  };

  const US_STATES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  const SectionHeader = ({ title, icon: Icon, section, color }) => (
    <button
      onClick={() => toggleSection(section)}
      className={`w-full flex items-center justify-between p-4 bg-${color}-50 border border-${color}-200 rounded-lg hover:bg-${color}-100 transition-colors`}
    >
      <div className="flex items-center gap-3">
        <Icon className={`w-5 h-5 text-${color}-600`} />
        <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
      </div>
      {expandedSections[section] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
    </button>
  );

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-slate-900 mb-2">Quick Profile Input</h2>
        <p className="text-slate-600">
          Enter your academic profile data. This will be used to generate realistic parent insights and assessment questions.
        </p>
      </div>

      {/* Basic Info */}
      <div>
        <SectionHeader title="Basic Information" icon={User} section="basic" color="azure" />
        {expandedSections.basic && (
          <div className="mt-4 p-4 border border-slate-200 rounded-lg space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Student Name *</label>
                <input
                  type="text"
                  value={profile.name || ''}
                  onChange={(e) => updateProfile('name', e.target.value)}
                  placeholder="Enter student name"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Graduation Year *</label>
                <select
                  value={profile.graduation_year || ''}
                  onChange={(e) => updateProfile('graduation_year', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                >
                  <option value="">Select year</option>
                  {[2024, 2025, 2026, 2027, 2028].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Grade Level</label>
                <select
                  value={profile.grade_level || ''}
                  onChange={(e) => updateProfile('grade_level', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                >
                  <option value="">Select grade</option>
                  <option value="9th">9th Grade</option>
                  <option value="10th">10th Grade</option>
                  <option value="11th">11th Grade</option>
                  <option value="12th">12th Grade</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">State of Residency *</label>
                <select
                  value={profile.state_of_residency || ''}
                  onChange={(e) => updateProfile('state_of_residency', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                >
                  <option value="">Select state</option>
                  {US_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">High School Name</label>
                <input
                  type="text"
                  value={profile.high_school_name || ''}
                  onChange={(e) => updateProfile('high_school_name', e.target.value)}
                  placeholder="e.g., Lincoln High School"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">City</label>
                <input
                  type="text"
                  value={profile.high_school_city || ''}
                  onChange={(e) => updateProfile('high_school_city', e.target.value)}
                  placeholder="e.g., Portland"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">State</label>
                <select
                  value={profile.high_school_state || ''}
                  onChange={(e) => updateProfile('high_school_state', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                >
                  <option value="">Select state</option>
                  {US_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Academic Stats */}
      <div>
        <SectionHeader title="Academic Performance" icon={BarChart3} section="academic" color="emerald" />
        {expandedSections.academic && (
          <div className="mt-4 p-4 border border-slate-200 rounded-lg space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Weighted GPA *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="5.0"
                  value={profile.gpa_weighted || ''}
                  onChange={(e) => updateProfile('gpa_weighted', parseFloat(e.target.value))}
                  placeholder="4.25"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Unweighted GPA</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="4.0"
                  value={profile.gpa_unweighted || ''}
                  onChange={(e) => updateProfile('gpa_unweighted', parseFloat(e.target.value))}
                  placeholder="3.95"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">GPA Scale</label>
                <select
                  value={profile.gpa_scale || '4.0'}
                  onChange={(e) => updateProfile('gpa_scale', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="4.0">4.0 Scale</option>
                  <option value="5.0">5.0 Scale</option>
                  <option value="100">100 Point Scale</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Class Rank</label>
                <input
                  type="number"
                  min="1"
                  value={profile.class_rank || ''}
                  onChange={(e) => updateProfile('class_rank', parseInt(e.target.value))}
                  placeholder="15"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Class Size</label>
                <input
                  type="number"
                  min="1"
                  value={profile.class_size || ''}
                  onChange={(e) => updateProfile('class_size', parseInt(e.target.value))}
                  placeholder="350"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Test Scores */}
      <div>
        <SectionHeader title="Test Scores" icon={FileText} section="scores" color="indigo" />
        {expandedSections.scores && (
          <div className="mt-4 p-4 border border-slate-200 rounded-lg space-y-4">
            <div>
              <h4 className="font-medium text-slate-900 mb-3">SAT Scores</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Total Score</label>
                  <input
                    type="number"
                    min="400"
                    max="1600"
                    value={profile.sat_total || ''}
                    onChange={(e) => updateProfile('sat_total', parseInt(e.target.value))}
                    placeholder="1520"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Math</label>
                  <input
                    type="number"
                    min="200"
                    max="800"
                    value={profile.sat_math || ''}
                    onChange={(e) => updateProfile('sat_math', parseInt(e.target.value))}
                    placeholder="780"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Reading/Writing</label>
                  <input
                    type="number"
                    min="200"
                    max="800"
                    value={profile.sat_reading_writing || ''}
                    onChange={(e) => updateProfile('sat_reading_writing', parseInt(e.target.value))}
                    placeholder="740"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-medium text-slate-900 mb-3">ACT Scores</h4>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Composite</label>
                  <input
                    type="number"
                    min="1"
                    max="36"
                    value={profile.act_composite || ''}
                    onChange={(e) => updateProfile('act_composite', parseInt(e.target.value))}
                    placeholder="34"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Math</label>
                  <input
                    type="number"
                    min="1"
                    max="36"
                    value={profile.act_math || ''}
                    onChange={(e) => updateProfile('act_math', parseInt(e.target.value))}
                    placeholder="35"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">English</label>
                  <input
                    type="number"
                    min="1"
                    max="36"
                    value={profile.act_english || ''}
                    onChange={(e) => updateProfile('act_english', parseInt(e.target.value))}
                    placeholder="33"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Reading</label>
                  <input
                    type="number"
                    min="1"
                    max="36"
                    value={profile.act_reading || ''}
                    onChange={(e) => updateProfile('act_reading', parseInt(e.target.value))}
                    placeholder="35"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Science</label>
                  <input
                    type="number"
                    min="1"
                    max="36"
                    value={profile.act_science || ''}
                    onChange={(e) => updateProfile('act_science', parseInt(e.target.value))}
                    placeholder="34"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Coursework & Rigor */}
      <div>
        <SectionHeader title="Coursework & Rigor" icon={GraduationCap} section="coursework" color="violet" />
        {expandedSections.coursework && (
          <div className="mt-4 p-4 border border-slate-200 rounded-lg space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Curriculum Rigor</label>
                <select
                  value={profile.curriculum_rigor || ''}
                  onChange={(e) => updateProfile('curriculum_rigor', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                >
                  <option value="">Select rigor level</option>
                  <option value="most_rigorous">Most Rigorous</option>
                  <option value="very_rigorous">Very Rigorous</option>
                  <option value="rigorous">Rigorous</option>
                  <option value="average">Average</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">AP Courses</label>
                <input
                  type="number"
                  min="0"
                  max="20"
                  value={profile.ap_courses_taken || ''}
                  onChange={(e) => updateProfile('ap_courses_taken', parseInt(e.target.value))}
                  placeholder="8"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Honors Courses</label>
                <input
                  type="number"
                  min="0"
                  value={profile.honors_courses || ''}
                  onChange={(e) => updateProfile('honors_courses', parseInt(e.target.value))}
                  placeholder="4"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Dual Enrollment Credits</label>
                <input
                  type="number"
                  min="0"
                  value={profile.dual_enrollment_credits || ''}
                  onChange={(e) => updateProfile('dual_enrollment_credits', parseInt(e.target.value))}
                  placeholder="12"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Interests & Goals */}
      <div>
        <SectionHeader title="Interests & Goals" icon={Target} section="interests" color="rose" />
        {expandedSections.interests && (
          <div className="mt-4 p-4 border border-slate-200 rounded-lg space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Intended Major *</label>
                <input
                  type="text"
                  value={profile.intended_major || ''}
                  onChange={(e) => updateProfile('intended_major', e.target.value)}
                  placeholder="e.g., Computer Science"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Intended Minor</label>
                <input
                  type="text"
                  value={profile.intended_minor || ''}
                  onChange={(e) => updateProfile('intended_minor', e.target.value)}
                  placeholder="e.g., Mathematics"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Career Interest</label>
                <input
                  type="text"
                  value={profile.intended_career || ''}
                  onChange={(e) => updateProfile('intended_career', e.target.value)}
                  placeholder="e.g., Software Engineer"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Career Goals</label>
              <textarea
                value={profile.career_goals || ''}
                onChange={(e) => updateProfile('career_goals', e.target.value)}
                placeholder="Describe your career aspirations and what you hope to achieve professionally..."
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Special Talents</label>
              <input
                type="text"
                value={profile.special_talents || ''}
                onChange={(e) => updateProfile('special_talents', e.target.value)}
                placeholder="e.g., Music, Art, Creative Writing"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
              />
            </div>
          </div>
        )}
      </div>

      {/* extracurriculars */}
      <div>
        <SectionHeader title="extracurriculars" icon={Users} section="extracurriculars" color="amber" />
        {expandedSections.extracurriculars && (
          <div className="mt-4 p-4 border border-slate-200 rounded-lg space-y-4">
            {profile.extracurriculars && profile.extracurriculars.length > 0 ? (
              profile.extracurriculars.map((activity, index) => (
                <div key={index} className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex justify-between items-start mb-3">
                    <span className="font-medium text-amber-900">Activity #{index + 1}</span>
                    <button
                      onClick={() => removeActivity(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="md:col-span-2">
                      <input
                        type="text"
                        value={activity.name || ''}
                        onChange={(e) => updateActivity(index, 'name', e.target.value)}
                        placeholder="Activity name"
                        className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                      />
                    </div>
                    <div>
                      <select
                        value={activity.category || 'academic'}
                        onChange={(e) => updateActivity(index, 'category', e.target.value)}
                        className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="academic">Academic</option>
                        <option value="arts">Arts & Music</option>
                        <option value="athletics">Athletics</option>
                        <option value="community_service">Community Service</option>
                        <option value="leadership">Leadership</option>
                        <option value="work">Work Experience</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <input
                        type="number"
                        min="0"
                        max="40"
                        value={activity.hours_per_week || ''}
                        onChange={(e) => updateActivity(index, 'hours_per_week', parseInt(e.target.value))}
                        placeholder="Hours per week"
                        className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                      />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-slate-500 text-center py-4">No extracurriculars added yet</p>
            )}
            <button
              onClick={addActivity}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors font-medium"
            >
              <Plus className="w-4 h-4" />
              Add Activity
            </button>
          </div>
        )}
      </div>

      {/* Honors */}
      <div>
        <SectionHeader title="Honors & Awards" icon={Trophy} section="honors" color="yellow" />
        {expandedSections.honors && (
          <div className="mt-4 p-4 border border-slate-200 rounded-lg space-y-4">
            {profile.honors && profile.honors.length > 0 ? (
              profile.honors.map((honor, index) => (
                <div key={index} className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex justify-between items-start mb-3">
                    <span className="font-medium text-yellow-900">Honor #{index + 1}</span>
                    <button
                      onClick={() => removeHonor(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="md:col-span-2">
                      <input
                        type="text"
                        value={honor.name || ''}
                        onChange={(e) => updateHonor(index, 'name', e.target.value)}
                        placeholder="Honor/Award name"
                        className="w-full px-3 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                      />
                    </div>
                    <div>
                      <select
                        value={honor.level || 'school'}
                        onChange={(e) => updateHonor(index, 'level', e.target.value)}
                        className="w-full px-3 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                      >
                        <option value="school">School Level</option>
                        <option value="local">Local/Regional</option>
                        <option value="state">State Level</option>
                        <option value="national">National Level</option>
                        <option value="international">International</option>
                      </select>
                    </div>
                    <div>
                      <input
                        type="date"
                        value={honor.date_received || ''}
                        onChange={(e) => updateHonor(index, 'date_received', e.target.value)}
                        className="w-full px-3 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                      />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-slate-500 text-center py-4">No honors added yet</p>
            )}
            <button
              onClick={addHonor}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors font-medium"
            >
              <Plus className="w-4 h-4" />
              Add Honor/Award
            </button>
          </div>
        )}
      </div>

      {/* Help text */}
      <div className="bg-azure-50 border border-azure-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-azure-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-azure-900 mb-1">Next Step: Parent Insights</h4>
            <p className="text-sm text-azure-700">
              Once you've entered the profile data, we'll use AI to generate realistic parent observations based on this information. You'll be able to review and edit them before proceeding.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step1_QuickProfileInput;
